import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calculator, Download, FileSpreadsheet, FileText, Trash2, Save, RefreshCw, AlertCircle, UserX, Pencil, Users } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import jsPDF from "jspdf";
import ExcelJS from "exceljs";

interface Employee {
  id: string;
  name: string;
  position: string;
  weekly_hours: number | null;
  vacation_days: number | null;
  is_non_productive: boolean;
}

interface CostCalculation {
  id: string;
  employee_id: string;
  calculation_name: string | null;
  calculation_date: string;
  brutto_monat: number;
  sollstunden_monat: number;
  krankenversicherung_prozent: number;
  rentenversicherung_prozent: number;
  pflegeversicherung_prozent: number;
  arbeitslosenversicherung_prozent: number;
  umlage_u1_prozent: number;
  umlage_u2_prozent: number;
  insolvenz_prozent: number;
  arbeitskleidung_monat: number;
  werkzeug_verschleiss_monat: number;
  psa_monat: number;
  schulungen_monat: number;
  handy_it_monat: number;
  fahrzeug_anteil_monat: number;
  verwaltung_hr_anteil_monat: number;
  sonstiges_monat: number;
  arbeitstage_jahr: number;
  urlaubstage: number;
  geschaetzte_krankheitstage: number;
  feiertage: number;
  produktive_tage_jahr: number;
  ziel_marge_prozent: number;
  risiko_puffer_prozent: number;
  overhead_prozent: number;
  ag_pflichtkosten: number;
  personalkosten_roh: number;
  nebenkosten_summe: number;
  gesamtkosten_roh: number;
  korrekturfaktor: number;
  produktive_kosten_monat: number;
  selbstkosten_pro_stunde: number;
  ziel_stundensatz: number;
  created_at: string;
}

interface FormData {
  // Block A
  brutto_monat: number;
  sollstunden_monat: number;
  // Block B
  krankenversicherung_prozent: number;
  rentenversicherung_prozent: number;
  pflegeversicherung_prozent: number;
  arbeitslosenversicherung_prozent: number;
  umlage_u1_prozent: number;
  umlage_u2_prozent: number;
  insolvenz_prozent: number;
  // Block C
  arbeitskleidung_monat: number;
  werkzeug_verschleiss_monat: number;
  psa_monat: number;
  schulungen_monat: number;
  handy_it_monat: number;
  fahrzeug_anteil_monat: number;
  verwaltung_hr_anteil_monat: number;
  sonstiges_monat: number;
  // Block D
  arbeitstage_jahr: number;
  urlaubstage: number;
  geschaetzte_krankheitstage: number;
  feiertage: number;
  unproduktivzeit_prozent: number;
  // Block E
  ziel_marge_prozent: number;
  risiko_puffer_prozent: number;
  overhead_prozent: number;
  // Meta
  calculation_name: string;
}

const defaultFormData: FormData = {
  brutto_monat: 0,
  sollstunden_monat: 173,
  krankenversicherung_prozent: 7.3,
  rentenversicherung_prozent: 9.3,
  pflegeversicherung_prozent: 1.7,
  arbeitslosenversicherung_prozent: 1.3,
  umlage_u1_prozent: 1.6,
  umlage_u2_prozent: 0.44,
  insolvenz_prozent: 0.06,
  arbeitskleidung_monat: 0,
  werkzeug_verschleiss_monat: 0,
  psa_monat: 0,
  schulungen_monat: 0,
  handy_it_monat: 0,
  fahrzeug_anteil_monat: 0,
  verwaltung_hr_anteil_monat: 0,
  sonstiges_monat: 0,
  arbeitstage_jahr: 250,
  urlaubstage: 30,
  geschaetzte_krankheitstage: 10,
  feiertage: 12,
  unproduktivzeit_prozent: 0,
  ziel_marge_prozent: 15,
  risiko_puffer_prozent: 5,
  overhead_prozent: 10,
  calculation_name: "",
};

export function CostCalculationSettings() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [history, setHistory] = useState<CostCalculation[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [unproductiveOverheadPerEmployee, setUnproductiveOverheadPerEmployee] = useState(0);
  const [unproductiveCount, setUnproductiveCount] = useState(0);
  const [productiveCount, setProductiveCount] = useState(0);
  const [savedCalcsByEmployee, setSavedCalcsByEmployee] = useState<Array<{
    employee_id: string;
    employee_name: string;
    employee_position: string;
    latest_id: string;
    latest_date: string;
    latest_name: string | null;
    latest_ziel_stundensatz: number;
    latest_selbstkosten_pro_stunde: number;
    count: number;
  }>>([]);

  // Load list of employees that already have stored calculations
  const loadSavedCalcsOverview = async () => {
    const { data, error } = await supabase
      .from("employee_cost_calculations")
      .select("id, employee_id, calculation_date, calculation_name, ziel_stundensatz, selbstkosten_pro_stunde, employees!inner(name, position, archived_at)")
      .order("calculation_date", { ascending: false });
    if (error) {
      console.error("Error loading saved calcs overview:", error);
      return;
    }
    const map = new Map<string, typeof savedCalcsByEmployee[number]>();
    (data || []).forEach((row: any) => {
      if (row.employees?.archived_at) return;
      const existing = map.get(row.employee_id);
      if (!existing) {
        map.set(row.employee_id, {
          employee_id: row.employee_id,
          employee_name: row.employees?.name || "—",
          employee_position: row.employees?.position || "",
          latest_id: row.id,
          latest_date: row.calculation_date,
          latest_name: row.calculation_name,
          latest_ziel_stundensatz: row.ziel_stundensatz || 0,
          latest_selbstkosten_pro_stunde: row.selbstkosten_pro_stunde || 0,
          count: 1,
        });
      } else {
        existing.count += 1;
      }
    });
    setSavedCalcsByEmployee(Array.from(map.values()).sort((a, b) => a.employee_name.localeCompare(b.employee_name)));
  };

  useEffect(() => {
    loadSavedCalcsOverview();
  }, []);

  // Load a stored calculation into the form for editing
  const loadStoredCalculation = async (employeeId: string, calcId: string) => {
    setSelectedEmployeeId(employeeId);
    setValidationErrors([]);
    const { data, error } = await supabase
      .from("employee_cost_calculations")
      .select("*")
      .eq("id", calcId)
      .maybeSingle();
    if (error || !data) {
      toast.error("Berechnung konnte nicht geladen werden");
      return;
    }
    setFormData({
      brutto_monat: data.brutto_monat || 0,
      sollstunden_monat: data.sollstunden_monat || 173,
      krankenversicherung_prozent: data.krankenversicherung_prozent || 0,
      rentenversicherung_prozent: data.rentenversicherung_prozent || 0,
      pflegeversicherung_prozent: data.pflegeversicherung_prozent || 0,
      arbeitslosenversicherung_prozent: data.arbeitslosenversicherung_prozent || 0,
      umlage_u1_prozent: data.umlage_u1_prozent || 0,
      umlage_u2_prozent: data.umlage_u2_prozent || 0,
      insolvenz_prozent: data.insolvenz_prozent || 0,
      arbeitskleidung_monat: data.arbeitskleidung_monat || 0,
      werkzeug_verschleiss_monat: data.werkzeug_verschleiss_monat || 0,
      psa_monat: data.psa_monat || 0,
      schulungen_monat: data.schulungen_monat || 0,
      handy_it_monat: data.handy_it_monat || 0,
      fahrzeug_anteil_monat: data.fahrzeug_anteil_monat || 0,
      verwaltung_hr_anteil_monat: data.verwaltung_hr_anteil_monat || 0,
      sonstiges_monat: data.sonstiges_monat || 0,
      arbeitstage_jahr: data.arbeitstage_jahr || 250,
      urlaubstage: data.urlaubstage || 30,
      geschaetzte_krankheitstage: data.geschaetzte_krankheitstage || 10,
      feiertage: data.feiertage || 12,
      unproduktivzeit_prozent: 0,
      ziel_marge_prozent: data.ziel_marge_prozent || 15,
      risiko_puffer_prozent: data.risiko_puffer_prozent || 5,
      overhead_prozent: data.overhead_prozent || 10,
      calculation_name: data.calculation_name || "",
    });
    toast.success("Gespeicherte Berechnung geladen – Anpassen & Speichern erstellt eine neue Version");
  };


  // Load employees
  useEffect(() => {
    const loadEmployees = async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("id, name, position, weekly_hours, vacation_days, is_non_productive")
        .is("archived_at", null)
        .order("name");
      
      if (error) {
        console.error("Error loading employees:", error);
        return;
      }
      setEmployees((data || []).map(e => ({ ...e, is_non_productive: e.is_non_productive ?? false })));
    };
    loadEmployees();
  }, []);

  // Load unproductive overhead distribution
  const loadUnproductiveOverhead = async () => {
    // Get all non-productive employees
    const nonProductiveEmps = employees.filter(e => e.is_non_productive);
    // Get all productive employees (unproduktivzeit < 50% means ≥50% productive)
    const productiveEmps = employees.filter(e => !e.is_non_productive);
    
    setUnproductiveCount(nonProductiveEmps.length);
    setProductiveCount(productiveEmps.length);

    if (nonProductiveEmps.length === 0 || productiveEmps.length === 0) {
      setUnproductiveOverheadPerEmployee(0);
      return;
    }

    // Load latest gesamtkosten_roh for each non-productive employee
    let totalUnproductiveCosts = 0;
    for (const emp of nonProductiveEmps) {
      const { data } = await supabase
        .from("employee_cost_calculations")
        .select("gesamtkosten_roh")
        .eq("employee_id", emp.id)
        .order("calculation_date", { ascending: false })
        .limit(1);
      if (data && data.length > 0) {
        totalUnproductiveCosts += data[0].gesamtkosten_roh || 0;
      }
    }

    const overheadPerEmployee = totalUnproductiveCosts / productiveEmps.length;
    setUnproductiveOverheadPerEmployee(round2(overheadPerEmployee));
  };

  useEffect(() => {
    if (employees.length > 0) {
      loadUnproductiveOverhead();
    }
  }, [employees]);

  // Load history when employee changes
  useEffect(() => {
    if (!selectedEmployeeId) {
      setHistory([]);
      return;
    }

    const loadHistory = async () => {
      const { data, error } = await supabase
        .from("employee_cost_calculations")
        .select("*")
        .eq("employee_id", selectedEmployeeId)
        .order("calculation_date", { ascending: false });
      
      if (error) {
        console.error("Error loading history:", error);
        return;
      }
      setHistory((data || []) as CostCalculation[]);
    };
    loadHistory();
  }, [selectedEmployeeId]);

  // Load employee data when selected
  const loadEmployeeData = async (employeeId: string) => {
    setLoadingEmployee(true);
    try {
      const employee = employees.find(e => e.id === employeeId);
      if (!employee) return;

      // Get latest salary
      const { data: salaryData } = await supabase
        .from("salary_history")
        .select("base_salary")
        .eq("employee_id", employeeId)
        .order("effective_date", { ascending: false })
        .limit(1);

      // Get tools costs (monthly depreciation estimate)
      const { data: toolsData } = await supabase
        .from("employee_tools")
        .select("replacement_cost, is_assigned")
        .eq("employee_id", employeeId)
        .eq("is_assigned", true);

      // Get assigned vehicle (fixed monthly costs: leasing + insurance + tax/12)
      const { data: vehicleData } = await supabase
        .from("vehicles")
        .select("id, leasing_rate, insurance_monthly, tax_yearly")
        .eq("responsible_employee_id", employeeId)
        .limit(1)
        .maybeSingle();

      // Get recurring other costs (vehicle_costs with payment_frequency)
      const { data: otherCostsData } = await supabase
        .from("vehicle_costs")
        .select("amount, payment_frequency")
        .eq("employee_id", employeeId);

      // Get SIM card costs (Handy/IT)
      const { data: simData } = await supabase
        .from("sim_cards")
        .select("monthly_cost")
        .eq("assigned_employee_id", employeeId)
        .eq("is_active", true);

      // Calculate monthly values
      const brutto = salaryData?.[0]?.base_salary || 0;
      const weeklyHours = employee.weekly_hours || 40;
      const sollstunden = Math.round(weeklyHours * 4.33 * 100) / 100;
      const urlaubstage = employee.vacation_days || 30;

      // Tools: estimate monthly cost as replacement_cost / 36 (3 years depreciation)
      const werkzeugKosten = (toolsData || []).reduce((sum, tool) => {
        return sum + ((tool.replacement_cost || 0) / 36);
      }, 0);

      // Vehicle fixed costs: leasing + insurance + tax/12
      const fahrzeugKosten = vehicleData
        ? (vehicleData.leasing_rate || 0) +
          (vehicleData.insurance_monthly || 0) +
          ((vehicleData.tax_yearly || 0) / 12)
        : 0;

      // Other recurring costs -> monthly equivalent
      const sonstigeKosten = (otherCostsData || []).reduce((sum, cost) => {
        const amount = cost.amount || 0;
        switch (cost.payment_frequency) {
          case "yearly": return sum + (amount / 12);
          case "half_yearly": return sum + (amount / 6);
          case "quarterly": return sum + (amount / 3);
          default: return sum + amount;
        }
      }, 0);

      // SIM / Handy costs
      const handyKosten = (simData || []).reduce((sum, sim) => sum + (sim.monthly_cost || 0), 0);

      setFormData(prev => ({
        ...prev,
        brutto_monat: brutto,
        sollstunden_monat: sollstunden,
        urlaubstage: urlaubstage,
        werkzeug_verschleiss_monat: Math.round(werkzeugKosten * 100) / 100,
        fahrzeug_anteil_monat: Math.round(fahrzeugKosten * 100) / 100,
        sonstiges_monat: Math.round(sonstigeKosten * 100) / 100,
        handy_it_monat: Math.round(handyKosten * 100) / 100,
      }));

      toast.success("Mitarbeiterdaten geladen");
    } catch (error) {
      console.error("Error loading employee data:", error);
      toast.error("Fehler beim Laden der Mitarbeiterdaten");
    } finally {
      setLoadingEmployee(false);
    }
  };

  // Calculate results
  const results = useMemo(() => {
    const {
      brutto_monat,
      sollstunden_monat,
      krankenversicherung_prozent,
      rentenversicherung_prozent,
      pflegeversicherung_prozent,
      arbeitslosenversicherung_prozent,
      umlage_u1_prozent,
      umlage_u2_prozent,
      insolvenz_prozent,
      arbeitskleidung_monat,
      werkzeug_verschleiss_monat,
      psa_monat,
      schulungen_monat,
      handy_it_monat,
      fahrzeug_anteil_monat,
      verwaltung_hr_anteil_monat,
      sonstiges_monat,
      arbeitstage_jahr,
      urlaubstage,
      geschaetzte_krankheitstage,
      feiertage,
      ziel_marge_prozent,
      risiko_puffer_prozent,
      overhead_prozent,
    } = formData;

    // Step 1: Bruttolohn
    const bruttolohn_monat = brutto_monat;

    // Step 2: AG-Pflichtkosten
    const ag_prozent_summe = 
      krankenversicherung_prozent +
      rentenversicherung_prozent +
      pflegeversicherung_prozent +
      arbeitslosenversicherung_prozent +
      umlage_u1_prozent +
      umlage_u2_prozent +
      insolvenz_prozent;
    const ag_pflichtkosten = brutto_monat * (ag_prozent_summe / 100);

    // Step 3: Personalkosten roh
    const personalkosten_roh = brutto_monat + ag_pflichtkosten;

    // Step 4: Nebenkosten
    const nebenkosten_summe = 
      arbeitskleidung_monat +
      werkzeug_verschleiss_monat +
      psa_monat +
      schulungen_monat +
      handy_it_monat +
      fahrzeug_anteil_monat +
      verwaltung_hr_anteil_monat +
      sonstiges_monat;

    // Step 5: Gesamtkosten roh (eigene Kosten)
    const gesamtkosten_roh = personalkosten_roh + nebenkosten_summe;

    // Step 5b: Umlage unproduktiver Mitarbeiter
    const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
    const isCurrentNonProductive = selectedEmployee?.is_non_productive || false;
    const gesamtkosten_inkl_umlage = isCurrentNonProductive 
      ? gesamtkosten_roh  // Non-productive: no overhead added (their cost IS the overhead)
      : gesamtkosten_roh + unproductiveOverheadPerEmployee;

    // Step 6: Produktivitätskorrektur via Unproduktivzeiten
    const produktive_tage = Math.max(1, arbeitstage_jahr - urlaubstage - geschaetzte_krankheitstage - feiertage);
    const unproduktivzeit_faktor = 1 - (formData.unproduktivzeit_prozent / 100);
    const produktive_stunden_monat = sollstunden_monat * Math.max(0, unproduktivzeit_faktor);
    const produktive_stunden_jahr = produktive_stunden_monat * 12;

    // Step 7: Selbstkosten pro Stunde (inkl. Umlage)
    const selbstkosten_pro_stunde = isCurrentNonProductive 
      ? 0  // Non-productive employees have no hourly rate
      : (produktive_stunden_monat > 0 ? gesamtkosten_inkl_umlage / produktive_stunden_monat : 0);

    // Step 9: Ziel-Stundensatz
    const zuschlag_gesamt = ziel_marge_prozent + risiko_puffer_prozent + overhead_prozent;
    const ziel_stundensatz = selbstkosten_pro_stunde * (1 + zuschlag_gesamt / 100);

    return {
      bruttolohn_monat: round2(bruttolohn_monat),
      ag_prozent_summe: round2(ag_prozent_summe),
      ag_pflichtkosten: round2(ag_pflichtkosten),
      personalkosten_roh: round2(personalkosten_roh),
      nebenkosten_summe: round2(nebenkosten_summe),
      gesamtkosten_roh: round2(gesamtkosten_roh),
      gesamtkosten_inkl_umlage: round2(gesamtkosten_inkl_umlage),
      umlage_unproduktiv: round2(isCurrentNonProductive ? 0 : unproductiveOverheadPerEmployee),
      produktive_tage,
      produktive_stunden_monat: round2(produktive_stunden_monat),
      produktive_stunden_jahr: round2(produktive_stunden_jahr),
      selbstkosten_pro_stunde: round2(selbstkosten_pro_stunde),
      zuschlag_gesamt: round2(zuschlag_gesamt),
      ziel_stundensatz: round2(ziel_stundensatz),
      is_non_productive: isCurrentNonProductive,
    };
  }, [formData, unproductiveOverheadPerEmployee, employees, selectedEmployeeId]);

  // Validation
  const validate = (): string[] => {
    const errors: string[] = [];
    
    if (formData.brutto_monat <= 0) errors.push("Brutto/Monat muss größer als 0 sein");
    if (formData.sollstunden_monat <= 0) errors.push("Sollstunden muss größer als 0 sein");
    
    const produktiveTage = formData.arbeitstage_jahr - formData.urlaubstage - formData.geschaetzte_krankheitstage - formData.feiertage;
    if (produktiveTage <= 0) errors.push("Produktive Tage muss größer als 0 sein");
    if (produktiveTage > formData.arbeitstage_jahr) errors.push("Produktive Tage darf nicht größer als Arbeitstage sein");
    
    const percentFields = [
      { name: "Krankenversicherung", value: formData.krankenversicherung_prozent },
      { name: "Rentenversicherung", value: formData.rentenversicherung_prozent },
      { name: "Pflegeversicherung", value: formData.pflegeversicherung_prozent },
      { name: "Arbeitslosenversicherung", value: formData.arbeitslosenversicherung_prozent },
      { name: "Umlage U1", value: formData.umlage_u1_prozent },
      { name: "Umlage U2", value: formData.umlage_u2_prozent },
      { name: "Insolvenzumlage", value: formData.insolvenz_prozent },
      { name: "Zielmarge", value: formData.ziel_marge_prozent },
      { name: "Risikopuffer", value: formData.risiko_puffer_prozent },
      { name: "Overhead", value: formData.overhead_prozent },
    ];
    
    percentFields.forEach(field => {
      if (field.value < 0 || field.value > 100) {
        errors.push(`${field.name} muss zwischen 0 und 100 liegen`);
      }
    });

    return errors;
  };

  // Save calculation
  const handleSave = async () => {
    if (!selectedEmployeeId) {
      toast.error("Bitte wählen Sie einen Mitarbeiter aus");
      return;
    }

    const errors = validate();
    setValidationErrors(errors);
    
    if (errors.length > 0) {
      toast.error("Validierungsfehler - Berechnung nicht möglich");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("employee_cost_calculations")
        .insert({
          employee_id: selectedEmployeeId,
          calculation_name: formData.calculation_name || null,
          brutto_monat: formData.brutto_monat,
          sollstunden_monat: formData.sollstunden_monat,
          krankenversicherung_prozent: formData.krankenversicherung_prozent,
          rentenversicherung_prozent: formData.rentenversicherung_prozent,
          pflegeversicherung_prozent: formData.pflegeversicherung_prozent,
          arbeitslosenversicherung_prozent: formData.arbeitslosenversicherung_prozent,
          umlage_u1_prozent: formData.umlage_u1_prozent,
          umlage_u2_prozent: formData.umlage_u2_prozent,
          insolvenz_prozent: formData.insolvenz_prozent,
          arbeitskleidung_monat: formData.arbeitskleidung_monat,
          werkzeug_verschleiss_monat: formData.werkzeug_verschleiss_monat,
          psa_monat: formData.psa_monat,
          schulungen_monat: formData.schulungen_monat,
          handy_it_monat: formData.handy_it_monat,
          fahrzeug_anteil_monat: formData.fahrzeug_anteil_monat,
          verwaltung_hr_anteil_monat: formData.verwaltung_hr_anteil_monat,
          sonstiges_monat: formData.sonstiges_monat,
          arbeitstage_jahr: formData.arbeitstage_jahr,
          urlaubstage: formData.urlaubstage,
          geschaetzte_krankheitstage: formData.geschaetzte_krankheitstage,
          feiertage: formData.feiertage,
          ziel_marge_prozent: formData.ziel_marge_prozent,
          risiko_puffer_prozent: formData.risiko_puffer_prozent,
          overhead_prozent: formData.overhead_prozent,
          ag_pflichtkosten: results.ag_pflichtkosten,
          personalkosten_roh: results.personalkosten_roh,
          nebenkosten_summe: results.nebenkosten_summe,
          gesamtkosten_roh: results.gesamtkosten_roh,
          korrekturfaktor: 1,
          produktive_kosten_monat: results.gesamtkosten_roh,
          selbstkosten_pro_stunde: results.selbstkosten_pro_stunde,
          ziel_stundensatz: results.ziel_stundensatz,
          created_by: user?.id,
        });

      if (error) throw error;

      toast.success("Berechnung gespeichert");
      
      // Reload history
      const { data } = await supabase
        .from("employee_cost_calculations")
        .select("*")
        .eq("employee_id", selectedEmployeeId)
        .order("calculation_date", { ascending: false });
      setHistory((data || []) as CostCalculation[]);
      loadSavedCalcsOverview();
    } catch (error) {
      console.error("Error saving calculation:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  // Delete calculation
  const handleDelete = async (id: string) => {
    if (!confirm("Berechnung wirklich löschen?")) return;

    try {
      const { error } = await supabase
        .from("employee_cost_calculations")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setHistory(prev => prev.filter(h => h.id !== id));
      loadSavedCalcsOverview();
      toast.success("Berechnung gelöscht");
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    const employee = employees.find(e => e.id === selectedEmployeeId);
    if (!employee) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(18);
    doc.text("Einstandslohn-Berechnung", pageWidth / 2, y, { align: "center" });
    y += 10;

    doc.setFontSize(12);
    doc.text(`Mitarbeiter: ${employee.name}`, 20, y);
    y += 7;
    doc.text(`Datum: ${format(new Date(), "dd.MM.yyyy", { locale: de })}`, 20, y);
    y += 15;

    // Block A
    doc.setFontSize(14);
    doc.text("Block A: Lohnbasis", 20, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(`Brutto/Monat: ${formatCurrency(formData.brutto_monat)}`, 25, y);
    y += 5;
    doc.text(`Sollstunden/Monat: ${formData.sollstunden_monat}`, 25, y);
    y += 5;
    doc.text(`→ Bruttolohn/Monat: ${formatCurrency(results.bruttolohn_monat)}`, 25, y);
    y += 10;

    // Block B
    doc.setFontSize(14);
    doc.text("Block B: Arbeitgeberpflichtkosten", 20, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(`Krankenversicherung: ${formData.krankenversicherung_prozent}%`, 25, y);
    y += 5;
    doc.text(`Rentenversicherung: ${formData.rentenversicherung_prozent}%`, 25, y);
    y += 5;
    doc.text(`Pflegeversicherung: ${formData.pflegeversicherung_prozent}%`, 25, y);
    y += 5;
    doc.text(`Arbeitslosenversicherung: ${formData.arbeitslosenversicherung_prozent}%`, 25, y);
    y += 5;
    doc.text(`Umlage U1: ${formData.umlage_u1_prozent}%`, 25, y);
    y += 5;
    doc.text(`Umlage U2: ${formData.umlage_u2_prozent}%`, 25, y);
    y += 5;
    doc.text(`Insolvenzumlage: ${formData.insolvenz_prozent}%`, 25, y);
    y += 5;
    doc.text(`→ AG-Pflichtkosten: ${formatCurrency(results.ag_pflichtkosten)} (${results.ag_prozent_summe}%)`, 25, y);
    y += 5;
    doc.text(`→ Personalkosten roh: ${formatCurrency(results.personalkosten_roh)}`, 25, y);
    y += 10;

    // Block C
    doc.setFontSize(14);
    doc.text("Block C: Nebenkosten (monatlich)", 20, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(`Arbeitskleidung: ${formatCurrency(formData.arbeitskleidung_monat)}`, 25, y);
    y += 5;
    doc.text(`Werkzeug: ${formatCurrency(formData.werkzeug_verschleiss_monat)}`, 25, y);
    y += 5;
    doc.text(`PSA: ${formatCurrency(formData.psa_monat)}`, 25, y);
    y += 5;
    doc.text(`Schulungen: ${formatCurrency(formData.schulungen_monat)}`, 25, y);
    y += 5;
    doc.text(`Handy/IT: ${formatCurrency(formData.handy_it_monat)}`, 25, y);
    y += 5;
    doc.text(`Fahrzeug: ${formatCurrency(formData.fahrzeug_anteil_monat)}`, 25, y);
    y += 5;
    doc.text(`Verwaltung/HR: ${formatCurrency(formData.verwaltung_hr_anteil_monat)}`, 25, y);
    y += 5;
    doc.text(`Sonstiges: ${formatCurrency(formData.sonstiges_monat)}`, 25, y);
    y += 5;
    doc.text(`→ Nebenkosten Summe: ${formatCurrency(results.nebenkosten_summe)}`, 25, y);
    y += 5;
    doc.text(`→ Gesamtkosten roh: ${formatCurrency(results.gesamtkosten_roh)}`, 25, y);
    y += 10;

    // Block D
    doc.setFontSize(14);
    doc.text("Block D: Produktivitätskorrektur", 20, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(`Arbeitstage/Jahr: ${formData.arbeitstage_jahr}`, 25, y);
    y += 5;
    doc.text(`Urlaubstage: ${formData.urlaubstage}`, 25, y);
    y += 5;
    doc.text(`Krankheitstage (geschätzt): ${formData.geschaetzte_krankheitstage}`, 25, y);
    y += 5;
    doc.text(`Feiertage: ${formData.feiertage}`, 25, y);
    y += 5;
    doc.text(`Unproduktivzeiten: ${formData.unproduktivzeit_prozent}%`, 25, y);
    y += 5;
    doc.text(`→ Produktive Tage/Jahr: ${results.produktive_tage}`, 25, y);
    y += 5;
    doc.text(`→ Produktive Stunden/Monat: ${results.produktive_stunden_monat} h`, 25, y);
    y += 5;
    doc.text(`→ Produktive Stunden/Jahr: ${results.produktive_stunden_jahr} h`, 25, y);
    y += 10;

    // Block E - Results
    doc.setFontSize(14);
    doc.text("Block E: Stundenkosten & Ziel", 20, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(`Zielmarge: ${formData.ziel_marge_prozent}%`, 25, y);
    y += 5;
    doc.text(`Risikopuffer: ${formData.risiko_puffer_prozent}%`, 25, y);
    y += 5;
    doc.text(`Overhead: ${formData.overhead_prozent}%`, 25, y);
    y += 10;

    doc.setFontSize(14);
    doc.setTextColor(0, 100, 0);
    doc.text(`SELBSTKOSTEN PRO STUNDE: ${formatCurrency(results.selbstkosten_pro_stunde)}`, 25, y);
    y += 8;
    doc.setTextColor(0, 0, 150);
    doc.text(`ZIEL-STUNDENSATZ: ${formatCurrency(results.ziel_stundensatz)}`, 25, y);

    doc.save(`einstandslohn_${employee.name.replace(/\s+/g, "_")}_${format(new Date(), "yyyy-MM-dd")}.pdf`);
    toast.success("PDF exportiert");
  };

  // Export to Excel
  const exportToExcel = async () => {
    const employee = employees.find(e => e.id === selectedEmployeeId);
    if (!employee) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Einstandslohn");

    const data = [
      ["Einstandslohn-Berechnung"],
      ["Mitarbeiter", employee.name],
      ["Datum", format(new Date(), "dd.MM.yyyy")],
      [],
      ["BLOCK A: LOHNBASIS"],
      ["Brutto/Monat", formData.brutto_monat],
      ["Sollstunden/Monat", formData.sollstunden_monat],
      ["→ Bruttolohn/Monat", results.bruttolohn_monat],
      [],
      ["BLOCK B: ARBEITGEBERPFLICHTKOSTEN"],
      ["Krankenversicherung (%)", formData.krankenversicherung_prozent],
      ["Rentenversicherung (%)", formData.rentenversicherung_prozent],
      ["Pflegeversicherung (%)", formData.pflegeversicherung_prozent],
      ["Arbeitslosenversicherung (%)", formData.arbeitslosenversicherung_prozent],
      ["Umlage U1 (%)", formData.umlage_u1_prozent],
      ["Umlage U2 (%)", formData.umlage_u2_prozent],
      ["Insolvenzumlage (%)", formData.insolvenz_prozent],
      ["→ AG-Pflichtkosten", results.ag_pflichtkosten],
      ["→ Personalkosten roh", results.personalkosten_roh],
      [],
      ["BLOCK C: NEBENKOSTEN (monatlich)"],
      ["Arbeitskleidung", formData.arbeitskleidung_monat],
      ["Werkzeug", formData.werkzeug_verschleiss_monat],
      ["PSA", formData.psa_monat],
      ["Schulungen", formData.schulungen_monat],
      ["Handy/IT", formData.handy_it_monat],
      ["Fahrzeug", formData.fahrzeug_anteil_monat],
      ["Verwaltung/HR", formData.verwaltung_hr_anteil_monat],
      ["Sonstiges", formData.sonstiges_monat],
      ["→ Nebenkosten Summe", results.nebenkosten_summe],
      ["→ Gesamtkosten roh", results.gesamtkosten_roh],
      [],
      ["BLOCK D: PRODUKTIVITÄTSKORREKTUR"],
      ["Arbeitstage/Jahr", formData.arbeitstage_jahr],
      ["Urlaubstage", formData.urlaubstage],
      ["Krankheitstage (geschätzt)", formData.geschaetzte_krankheitstage],
      ["Feiertage", formData.feiertage],
      ["Unproduktivzeiten (%)", formData.unproduktivzeit_prozent],
      ["→ Produktive Tage/Jahr", results.produktive_tage],
      ["→ Produktive Stunden/Monat", results.produktive_stunden_monat],
      ["→ Produktive Stunden/Jahr", results.produktive_stunden_jahr],
      [],
      ["BLOCK E: STUNDENKOSTEN & ZIEL"],
      ["Zielmarge (%)", formData.ziel_marge_prozent],
      ["Risikopuffer (%)", formData.risiko_puffer_prozent],
      ["Overhead (%)", formData.overhead_prozent],
      [],
      ["SELBSTKOSTEN PRO STUNDE", results.selbstkosten_pro_stunde],
      ["ZIEL-STUNDENSATZ", results.ziel_stundensatz],
    ];

    data.forEach((row) => {
      worksheet.addRow(row);
    });

    // Style header row
    worksheet.getRow(1).font = { bold: true, size: 14 };
    
    // Auto-size columns
    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell?.({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value ? String(cell.value) : "";
        maxLength = Math.max(maxLength, cellValue.length);
      });
      column.width = Math.min(maxLength + 2, 50);
    });

    // Generate buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `einstandslohn_${employee.name.replace(/\s+/g, "_")}_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success("Excel exportiert");
  };

  // Toggle non-productive status
  const handleToggleNonProductive = async (employeeId: string, isNonProductive: boolean) => {
    try {
      const { error } = await supabase
        .from("employees")
        .update({ is_non_productive: isNonProductive } as any)
        .eq("id", employeeId);
      if (error) throw error;
      setEmployees(prev => prev.map(e => e.id === employeeId ? { ...e, is_non_productive: isNonProductive } : e));
      toast.success(isNonProductive ? "Als nicht-produktiv markiert" : "Als produktiv markiert");
    } catch (error) {
      console.error("Error toggling:", error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleEmployeeChange = (value: string) => {
    setSelectedEmployeeId(value);
    setFormData(defaultFormData);
    setValidationErrors([]);
    if (value) {
      loadEmployeeData(value);
    }
  };

  const updateField = (field: keyof FormData, value: number | string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationErrors([]);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Einstandslohn-Berechnung
          </CardTitle>
          <CardDescription>
            Berechnen Sie den Einstandslohn und Ziel-Stundensatz für Ihre Mitarbeiter
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Saved calculations overview */}
          {savedCalcsByEmployee.length > 0 && (
            <div className="rounded-lg border bg-card">
              <div className="flex items-center gap-2 px-4 py-3 border-b">
                <Users className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-medium text-sm">
                  Mitarbeiter mit gespeicherter Berechnung ({savedCalcsByEmployee.length})
                </h3>
              </div>
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mitarbeiter</TableHead>
                      <TableHead>Letzte Berechnung</TableHead>
                      <TableHead className="text-right">Selbstkosten/h</TableHead>
                      <TableHead className="text-right">Ziel/h</TableHead>
                      <TableHead className="text-right">Versionen</TableHead>
                      <TableHead className="text-right">Aktion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {savedCalcsByEmployee.map(row => (
                      <TableRow
                        key={row.employee_id}
                        className={selectedEmployeeId === row.employee_id ? "bg-muted/50" : ""}
                      >
                        <TableCell>
                          <div className="font-medium">{row.employee_name}</div>
                          <div className="text-xs text-muted-foreground">{row.employee_position}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {format(new Date(row.latest_date), "dd.MM.yyyy", { locale: de })}
                          {row.latest_name && (
                            <div className="text-xs text-muted-foreground">{row.latest_name}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(row.latest_selbstkosten_pro_stunde)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {formatCurrency(row.latest_ziel_stundensatz)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{row.count}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadStoredCalculation(row.employee_id, row.latest_id)}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Bearbeiten
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="px-4 py-2 text-xs text-muted-foreground border-t">
                "Bearbeiten" lädt die letzte gespeicherte Berechnung in das Formular. Beim Speichern wird eine neue Version angelegt; ältere Versionen bleiben im Verlauf erhalten.
              </p>
            </div>
          )}

          {/* Employee Selection */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label>Mitarbeiter auswählen</Label>
              <Select value={selectedEmployeeId} onValueChange={handleEmployeeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.is_non_productive && <UserX className="h-3 w-3 inline mr-1 text-muted-foreground" />}
                      {emp.name} - {emp.position}
                      {emp.is_non_productive && " (nicht produktiv)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedEmployeeId && (
              <Button 
                variant="outline" 
                onClick={() => loadEmployeeData(selectedEmployeeId)}
                disabled={loadingEmployee}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingEmployee ? 'animate-spin' : ''}`} />
                Daten aktualisieren
              </Button>
            )}
          </div>

          {/* Non-productive toggle */}
          {selectedEmployeeId && (
            <div className="flex items-center justify-between bg-muted/50 p-4 rounded-lg border">
              <div className="flex items-center gap-3">
                <UserX className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Nicht-produktiver Mitarbeiter</p>
                  <p className="text-xs text-muted-foreground">
                    Kosten werden auf alle produktiven Mitarbeiter umgelegt (keine eigenen Produktivstunden)
                  </p>
                </div>
              </div>
              <Switch
                checked={employees.find(e => e.id === selectedEmployeeId)?.is_non_productive || false}
                onCheckedChange={(checked) => handleToggleNonProductive(selectedEmployeeId, checked)}
              />
            </div>
          )}

          {/* Overhead info */}
          {unproductiveCount > 0 && productiveCount > 0 && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4 rounded-lg">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Kostenumlage aktiv: {unproductiveCount} nicht-produktive(r) Mitarbeiter → {formatCurrency(unproductiveOverheadPerEmployee)}/Monat auf {productiveCount} produktive Mitarbeiter verteilt
              </p>
            </div>
          )}
          {validationErrors.length > 0 && (
            <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
              <div className="flex items-center gap-2 text-destructive font-medium mb-2">
                <AlertCircle className="h-4 w-4" />
                Validierungsfehler
              </div>
              <ul className="list-disc list-inside text-sm text-destructive">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {selectedEmployeeId && (
            <>
              {/* Calculation Name */}
              <div>
                <Label>Berechnungsname (optional)</Label>
                <Input
                  value={formData.calculation_name}
                  onChange={e => updateField("calculation_name", e.target.value)}
                  placeholder="z.B. Kalkulation Q1 2025"
                />
              </div>

              <Separator />

              {/* BLOCK A: Lohnbasis */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Badge variant="outline">A</Badge>
                  Lohnbasis
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Brutto/Monat (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.brutto_monat}
                      onChange={e => updateField("brutto_monat", parseFloat(e.target.value) || 0)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Auto-geladen aus Gehaltsverlauf</p>
                  </div>
                  <div>
                    <Label>Sollstunden/Monat</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.sollstunden_monat}
                      onChange={e => updateField("sollstunden_monat", parseFloat(e.target.value) || 0)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Wochenstunden × 4.33</p>
                  </div>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <span className="text-sm font-medium">→ Bruttolohn/Monat: </span>
                  <span className="text-lg font-bold">{formatCurrency(results.bruttolohn_monat)}</span>
                </div>
              </div>

              <Separator />

              {/* BLOCK B: Arbeitgeberpflichtkosten */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Badge variant="outline">B</Badge>
                  Arbeitgeberpflichtkosten
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <Label>Krankenvers. (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.krankenversicherung_prozent}
                      onChange={e => updateField("krankenversicherung_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Rentenvers. (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.rentenversicherung_prozent}
                      onChange={e => updateField("rentenversicherung_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Pflegevers. (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.pflegeversicherung_prozent}
                      onChange={e => updateField("pflegeversicherung_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Arbeitslosenvers. (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.arbeitslosenversicherung_prozent}
                      onChange={e => updateField("arbeitslosenversicherung_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Umlage U1 (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.umlage_u1_prozent}
                      onChange={e => updateField("umlage_u1_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Umlage U2 (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.umlage_u2_prozent}
                      onChange={e => updateField("umlage_u2_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Insolvenzumlage (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.insolvenz_prozent}
                      onChange={e => updateField("insolvenz_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg space-y-1">
                  <div>
                    <span className="text-sm font-medium">→ AG-Pflichtkosten: </span>
                    <span className="text-lg font-bold">{formatCurrency(results.ag_pflichtkosten)}</span>
                    <span className="text-sm text-muted-foreground ml-2">({results.ag_prozent_summe}%)</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium">→ Personalkosten roh: </span>
                    <span className="text-lg font-bold">{formatCurrency(results.personalkosten_roh)}</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* BLOCK C: Nebenkosten */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Badge variant="outline">C</Badge>
                  Nebenkosten (monatlich)
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <Label>Arbeitskleidung (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.arbeitskleidung_monat}
                      onChange={e => updateField("arbeitskleidung_monat", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Werkzeug (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.werkzeug_verschleiss_monat}
                      onChange={e => updateField("werkzeug_verschleiss_monat", parseFloat(e.target.value) || 0)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Auto-geladen</p>
                  </div>
                  <div>
                    <Label>PSA (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.psa_monat}
                      onChange={e => updateField("psa_monat", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Schulungen (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.schulungen_monat}
                      onChange={e => updateField("schulungen_monat", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Handy/IT (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.handy_it_monat}
                      onChange={e => updateField("handy_it_monat", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Fahrzeug (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.fahrzeug_anteil_monat}
                      onChange={e => updateField("fahrzeug_anteil_monat", parseFloat(e.target.value) || 0)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Auto-geladen</p>
                  </div>
                  <div>
                    <Label>Verwaltung/HR (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.verwaltung_hr_anteil_monat}
                      onChange={e => updateField("verwaltung_hr_anteil_monat", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Sonstiges (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.sonstiges_monat}
                      onChange={e => updateField("sonstiges_monat", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg space-y-1">
                  <div>
                    <span className="text-sm font-medium">→ Nebenkosten Summe: </span>
                    <span className="text-lg font-bold">{formatCurrency(results.nebenkosten_summe)}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium">→ Gesamtkosten roh: </span>
                    <span className="text-lg font-bold">{formatCurrency(results.gesamtkosten_roh)}</span>
                  </div>
                  {results.umlage_unproduktiv > 0 && (
                    <div>
                      <span className="text-sm font-medium">→ + Umlage nicht-produktiv: </span>
                      <span className="text-lg font-bold text-amber-600 dark:text-amber-400">{formatCurrency(results.umlage_unproduktiv)}</span>
                    </div>
                  )}
                  {results.umlage_unproduktiv > 0 && (
                    <div>
                      <span className="text-sm font-medium">→ Gesamtkosten inkl. Umlage: </span>
                      <span className="text-lg font-bold">{formatCurrency(results.gesamtkosten_inkl_umlage)}</span>
                    </div>
                  )}
                  {results.is_non_productive && (
                    <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        ⚠ Nicht-produktiver Mitarbeiter — Kosten werden auf produktive Mitarbeiter umgelegt
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* BLOCK D: Produktivität */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Badge variant="outline">D</Badge>
                  Produktivitätskorrektur
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <Label>Arbeitstage/Jahr</Label>
                    <Input
                      type="number"
                      value={formData.arbeitstage_jahr}
                      onChange={e => updateField("arbeitstage_jahr", parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Urlaubstage</Label>
                    <Input
                      type="number"
                      value={formData.urlaubstage}
                      onChange={e => updateField("urlaubstage", parseInt(e.target.value) || 0)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Auto-geladen</p>
                  </div>
                  <div>
                    <Label>Krankheitstage (gesch.)</Label>
                    <Input
                      type="number"
                      value={formData.geschaetzte_krankheitstage}
                      onChange={e => updateField("geschaetzte_krankheitstage", parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Feiertage</Label>
                    <Input
                      type="number"
                      value={formData.feiertage}
                      onChange={e => updateField("feiertage", parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Unproduktivzeiten (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.unproduktivzeit_prozent}
                      onChange={e => updateField("unproduktivzeit_prozent", parseFloat(e.target.value) || 0)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Anteil nicht-produktiver Arbeitszeit</p>
                  </div>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg space-y-1">
                  <div>
                    <span className="text-sm font-medium">→ Produktive Tage/Jahr: </span>
                    <span className="text-lg font-bold">{results.produktive_tage}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium">→ Produktive Stunden/Monat: </span>
                    <span className="text-lg font-bold">{results.produktive_stunden_monat} h</span>
                    {formData.unproduktivzeit_prozent > 0 && (
                      <span className="text-sm text-muted-foreground ml-2">(von {formData.sollstunden_monat} h, {formData.unproduktivzeit_prozent}% unproduktiv)</span>
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-medium">→ Produktive Stunden/Jahr: </span>
                    <span className="text-lg font-bold">{results.produktive_stunden_jahr} h</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* BLOCK E: Zuschläge */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Badge variant="outline">E</Badge>
                  Stundenkosten & Ziel
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Zielmarge (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.ziel_marge_prozent}
                      onChange={e => updateField("ziel_marge_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Risikopuffer (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.risiko_puffer_prozent}
                      onChange={e => updateField("risiko_puffer_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Overhead (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.overhead_prozent}
                      onChange={e => updateField("overhead_prozent", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                
                {/* Results */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div className="bg-green-100 dark:bg-green-900/30 p-4 rounded-lg border border-green-300 dark:border-green-700">
                    <p className="text-sm text-green-700 dark:text-green-300 font-medium">SELBSTKOSTEN PRO STUNDE</p>
                    <p className="text-3xl font-bold text-green-800 dark:text-green-200">{formatCurrency(results.selbstkosten_pro_stunde)}</p>
                  </div>
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-300 dark:border-blue-700">
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">ZIEL-STUNDENSATZ</p>
                    <p className="text-3xl font-bold text-blue-800 dark:text-blue-200">{formatCurrency(results.ziel_stundensatz)}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">inkl. {results.zuschlag_gesamt}% Zuschlag</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleSave} disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? "Speichern..." : "Berechnung speichern"}
                </Button>
                <Button variant="outline" onClick={exportToPDF}>
                  <FileText className="h-4 w-4 mr-2" />
                  PDF Export
                </Button>
                <Button variant="outline" onClick={exportToExcel}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Excel Export
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* History */}
      {selectedEmployeeId && history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Berechnungs-Historie</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Selbstkosten/h</TableHead>
                  <TableHead className="text-right">Ziel-Stunde</TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map(calc => (
                  <TableRow key={calc.id}>
                    <TableCell>
                      {format(new Date(calc.calculation_date), "dd.MM.yyyy", { locale: de })}
                    </TableCell>
                    <TableCell>{calc.calculation_name || "-"}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(calc.selbstkosten_pro_stunde)}
                    </TableCell>
                    <TableCell className="text-right font-medium text-primary">
                      {formatCurrency(calc.ziel_stundensatz)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(calc.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}
